/**
 * Named, curated country lists referenced from `CountryFilterCriteria.setName`.
 * Each set is a frozen ISO-A2 lookup; the filter intersects against it.
 *
 * Adding a new set: define the ISO array, push it through the
 * `Object.freeze(new Set(...))` shape so callers can't mutate it
 * accidentally. The filter resolution stays one indexed lookup.
 *
 * **Becomes admin-managed later.** Same story as the country overrides:
 * this file's shape is what the future CMS endpoint will return; swapping
 * the constant for a fetch is the only change needed.
 */

/**
 * UN member states as of 2026 — 193 countries. Sourced from the official
 * UN list (https://www.un.org/en/about-us/member-states). Notably excludes:
 *   - Vatican City (VA), Palestine (PS) — observer states, not members
 *   - Taiwan (TW), Kosovo (XK), Western Sahara (EH) — not recognized as
 *     full UN members
 *
 * Used by Seterra-style country quizzes so the pool matches what most
 * players consider the "standard" world country count.
 */
const UN_MEMBERS_RAW = [
  'AF', 'AL', 'DZ', 'AD', 'AO', 'AG', 'AR', 'AM', 'AU', 'AT',
  'AZ', 'BS', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BT',
  'BO', 'BA', 'BW', 'BR', 'BN', 'BG', 'BF', 'BI', 'CV', 'KH',
  'CM', 'CA', 'CF', 'TD', 'CL', 'CN', 'CO', 'KM', 'CG', 'CD',
  'CR', 'CI', 'HR', 'CU', 'CY', 'CZ', 'DK', 'DJ', 'DM', 'DO',
  'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'SZ', 'ET', 'FJ', 'FI',
  'FR', 'GA', 'GM', 'GE', 'DE', 'GH', 'GR', 'GD', 'GT', 'GN',
  'GW', 'GY', 'HT', 'HN', 'HU', 'IS', 'IN', 'ID', 'IR', 'IQ',
  'IE', 'IL', 'IT', 'JM', 'JP', 'JO', 'KZ', 'KE', 'KI', 'KP',
  'KR', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI',
  'LT', 'LU', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MH', 'MR',
  'MU', 'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MA', 'MZ', 'MM',
  'NA', 'NR', 'NP', 'NL', 'NZ', 'NI', 'NE', 'NG', 'MK', 'NO',
  'OM', 'PK', 'PW', 'PA', 'PG', 'PY', 'PE', 'PH', 'PL', 'PT',
  'QA', 'RO', 'RU', 'RW', 'KN', 'LC', 'VC', 'WS', 'SM', 'ST',
  'SA', 'SN', 'RS', 'SC', 'SL', 'SG', 'SK', 'SI', 'SB', 'SO',
  'ZA', 'SS', 'ES', 'LK', 'SD', 'SR', 'SE', 'CH', 'SY', 'TJ',
  'TZ', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TR', 'TM', 'TV',
  'UG', 'UA', 'AE', 'GB', 'US', 'UY', 'UZ', 'VU', 'VE', 'VN',
  'YE', 'ZM', 'ZW',
];

export const COUNTRY_SETS: Record<string, ReadonlySet<string>> = Object.freeze({
  'un-members': new Set(UN_MEMBERS_RAW),
});

/** Look up a curated set by name. Returns null for unknown names. */
export function getCountrySet(name: string): ReadonlySet<string> | null {
  return COUNTRY_SETS[name] ?? null;
}
